import { InfoCircleOutlined } from "@ant-design/icons"
import { Col, Form, Modal, Row, Space, Switch, Tooltip, Typography } from "antd"
import React from "react"

import { PhoneInput } from "../../../../components/PhoneInputs/PhoneInput"
import SelectComponent from "../../../../components/Select"
import { placeholders } from "../../../../constants/placeholders"
import { useAppSelector } from "../../../../hooks/use-app-selector"
import { normalizeEmail } from "../../../../utils/form/normalizeEmail.tsx"
import { validatePhone } from "../../../../utils/validation/validate-phone"
import { USER_DISABLED_STATUSES } from "../../constants/user-statuses.ts"
import { type TeamMemberMutationData } from "../../types/types.ts"
import { TeamMemberFormActions } from "./TeamMemberFormActions.tsx"
import { TeamMemberFormCommissionsSection } from "./TeamMemberFormCommissionsSection.tsx"
import { TeamMemberFormHeader } from "./TeamMemberFormHeader.tsx"
import { TeamMemberPermissionsRow } from "./TeamMemberPermissionsRow.tsx"
import { TextArea } from "../../../../components/Input/TextArea.tsx"
import { TextInput } from "../../../../components/Input/TextInput.tsx"

const { Text } = Typography
interface TeamMemberFormProps {
  initialValues?: TeamMemberMutationData
  isEdit: boolean
  isSelfEdit?: boolean
  isGccOwner?: boolean
  modifyPermissionsAllowed?: boolean
  inviteAllowed?: boolean
  onSave: (data: TeamMemberMutationData) => void
  onCancel: () => void
}

export const TeamMemberForm = ({
  initialValues,
  modifyPermissionsAllowed,
  inviteAllowed,
  isEdit,
  isSelfEdit,
  isGccOwner,
  onSave,
  onCancel
}: TeamMemberFormProps) => {
  // const dispatch = useAppDispatch()
  const [form] = Form.useForm<TeamMemberMutationData>()

  const saving = useAppSelector((state) => state.users.saveFetching)
  const filterOptions = useAppSelector((state) => state.users.filterOptions)
  const labelOptions = filterOptions?.labels || []
  const status = initialValues?.status

  const isNotEditableStatus =
    status && USER_DISABLED_STATUSES?.includes(status?.toLowerCase())

  // const handleGetDefaultPermissions = useCallback(
  //   async (labelId: string | number) => {
  //     try {
  //       const res = await dispatch(fetchLabelById(labelId)).unwrap()
  //       return res?.permissions
  //     } catch (e) {
  //       console.log(e)
  //     }
  //   },
  //   [fetchDefaultPermissions]
  // )

  const onFinish = ({
    permissions,
    label_id,
    last_name,
    ...values
  }: TeamMemberMutationData) => {
    // Delete sales fields if the role is not sales
    const salesFields = {
      office_main_lead_cost: values?.office_main_lead_cost || 0,
      office_referral_lead_cost: values?.office_referral_lead_cost || 0,
      sales_commission_ratio: values?.sales_commission_ratio || 0
    }

    const filteredSalesFields = permissions?.leads?.features
      ?.lead_assignable_as_sales
      ? salesFields
      : {
          office_main_lead_cost: undefined,
          office_referral_lead_cost: undefined,
          sales_commission_ratio: undefined
        }

    onSave({
      ...values,
      ...filteredSalesFields,
      // if label changed, because if you send label_id, then the API thinks that the label has changed and ignores the change in permissions
      ...(label_id !== initialValues?.label_id ? { label_id, last_name } : {}),
      // if it`s custom
      ...(modifyPermissionsAllowed && form.isFieldTouched("permissions")
        ? { permissions }
        : {}),
      // when invite is not allowed the form item is hidden so is_invite won't be in values — force it to false
      ...(!isEdit && inviteAllowed === false ? { is_invite: false } : {})
    } as TeamMemberMutationData)
  }

  return (
    <Modal
      open
      onCancel={onCancel}
      footer={null}
      width={800}
      title={<TeamMemberFormHeader isEdit={isEdit} form={form} />}
      maskClosable={false}
      centered
    >
      <Form<TeamMemberMutationData>
        form={form}
        layout="vertical"
        onFinish={onFinish}
        validateTrigger={["onSubmit", "onChange"]}
        className="with-default-error "
        disabled={saving}
        initialValues={{
          ...initialValues,
          is_invite: inviteAllowed !== false
        }}
      >
        {/* Name Fields */}
        <Row gutter={16}>
          <Col xs={24} sm={24} md={8}>
            <Form.Item<TeamMemberMutationData>
              label="First Name"
              name="first_name"
              rules={[
                {
                  required: true,
                  whitespace: true,
                  message: "Please enter first name"
                }
              ]}
            >
              <TextInput
                disabled={isNotEditableStatus || saving || isGccOwner}
                placeholder="First name"
                size="large"
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={8}>
            <Form.Item<TeamMemberMutationData>
              label="Middle Name"
              name="middle_name"
            >
              <TextInput
                disabled={isNotEditableStatus || saving || isGccOwner}
                placeholder="Middle name"
                size="large"
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={8}>
            <Form.Item<TeamMemberMutationData>
              label="Last Name"
              name="last_name"
              rules={[
                {
                  required: true,
                  whitespace: true,
                  message: "Please enter last name"
                }
              ]}
            >
              <TextInput
                disabled={isNotEditableStatus || saving || isGccOwner}
                placeholder="Last name"
                size="large"
              />
            </Form.Item>
          </Col>
        </Row>

        {/* Contact Fields */}
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item<TeamMemberMutationData>
              label="Email"
              name="email"
              rules={[
                { required: true, message: "Please enter email" },
                { type: "email", message: "Please enter a valid email" }
              ]}
              normalize={normalizeEmail}
            >
              <TextInput
                size="large"
                placeholder={placeholders.email}
                disabled={isNotEditableStatus || saving || isGccOwner} disableAutoCapitalize
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item<TeamMemberMutationData>
              label="Phone Number"
              name="phone"
              rules={[{ required: false, validator: validatePhone }]}
            >
              <PhoneInput
                disabled={isNotEditableStatus || saving || isGccOwner}
                placeholder={placeholders.phone}
                size="large"
              />
            </Form.Item>
          </Col>
        </Row>

        {/* Label & Permissions */}
        <TeamMemberPermissionsRow
          form={form}
          saving={saving}
          labelOptions={labelOptions}
          modifyPermissionsAllowed={modifyPermissionsAllowed}
          isSelfEdit={isSelfEdit}
          isGccOwner={isGccOwner}
        />

        {/* STATUS */}
        {isEdit && (
          <Form.Item<TeamMemberMutationData>
            label="Status"
            name="status"
            rules={[{ required: true, message: "Please select status" }]}
          >
            <SelectComponent
              size="large"
              placeholder="Select status"
              options={filterOptions?.["status"] || []}
              disabled
              optionFilterType="form"
            />
          </Form.Item>
        )}

        {/* Sales Commission Fields - only for SALES_MANAGER & SALES_PM */}
        <TeamMemberFormCommissionsSection form={form} />

        {/* Comment - only for edit mode */}
        {isEdit && (
          <Form.Item<TeamMemberMutationData>
            label="Comment"
            name="comment"
            className={"pb-4"}
          >
            <TextArea
              disabled={saving}
              size="large"
              rows={3}
              showCount
              maxLength={500}
              placeholder="Add your comment…"
            />
          </Form.Item>
        )}

        {!isEdit && inviteAllowed !== false && (
          <Form.Item>
            <Space>
              <Form.Item<TeamMemberMutationData>
                noStyle
                name="is_invite"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
              <Text>Send invitation</Text>
              <Tooltip title="If disabled, the team member will be added without invitation.">
                <InfoCircleOutlined />
              </Tooltip>
            </Space>
          </Form.Item>
        )}

        {/* register in the form */}
        <Form.Item<TeamMemberMutationData> name="permissions" hidden>
          <select />
        </Form.Item>

        {/* Form Actions */}
        <TeamMemberFormActions
          form={form}
          saving={saving}
          onCancel={onCancel}
          isEdit={isEdit}
        />
      </Form>
    </Modal>
  )
}
